// GAVION import duplicates (spec §3): the number of pairs waiting in « Qualité des données » → « Doublons », next to
// the admin settings entry (user menu, « Plus » sheet). Admins only; hidden at 0, 99+ above 99; refreshed every 5
// minutes and after a merge or a dismissal (DuplicatesPanel invalidates every DQ_QUERY_KEY query).
// GAVION suspect emails (89_): the count is now everything waiting there, duplicate pairs + « Courriels douteux »
// (data_quality_attention_count); SuspectEmailsPanel invalidates the same queries after an action.
// Review 2026-10-27: a dot on the closed user-menu button and the mobile « Plus » tab (DuplicatesDot), and the lead
// import invalidates the count when a run or an « Appliquer » ends.
import { useQuery } from "@tanstack/react-query";
import { usePermissions, useTranslate } from "ra-core";
import { useDataQualityClient } from "./api/useDataQualityClient";
import { DQ_QUERY_KEY } from "./dqErrors";

const POLL_MS = 5 * 60_000;

export function useDuplicateCount(): number {
  const { permissions } = usePermissions();
  const client = useDataQualityClient();
  const { data } = useQuery({
    queryKey: [...DQ_QUERY_KEY, "attention-count"],
    queryFn: () => client.attentionCount(),
    enabled: permissions === "admin",
    retry: false,
    staleTime: POLL_MS,
    refetchInterval: POLL_MS,
  });
  return permissions === "admin" ? (data ?? 0) : 0;
}

/** A dot on a closed menu trigger when something waits in « Qualité des données » (admins only). */
export const DuplicatesDot = ({ className = "" }: { className?: string }) => {
  const t = useTranslate();
  const n = useDuplicateCount();
  if (n === 0) return null;
  return (
    <span
      data-testid="data-quality-dot"
      title={t("gavion.data_quality.badge", { smart_count: n })}
      aria-hidden="true"
      className={`pointer-events-none absolute size-2.5 rounded-full bg-primary ring-2 ring-background ${className}`}
    />
  );
};

export const DuplicatesCount = () => {
  const t = useTranslate();
  const n = useDuplicateCount();
  if (n === 0) return null;
  return (
    <span
      className="ml-auto rounded-full bg-primary px-2 text-xs text-primary-foreground"
      aria-label={t("gavion.data_quality.badge", { smart_count: n })}
    >
      {n > 99 ? "99+" : String(n)}
    </span>
  );
};
